import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarClock, AlertCircle } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const pad = (n) => String(n).padStart(2, "0");

function toLocalInput(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Round up to the next clean 5-minute boundary.
function roundUp5(d) {
  const ms = 5 * 60 * 1000;
  return new Date(Math.ceil(d.getTime() / ms) * ms);
}

const QUICK = [
  { key: "today", label: "Today", days: 0 },
  { key: "tomorrow", label: "Tomorrow", days: 1 },
  { key: "dayafter", label: "Day After", days: 2 },
  { key: "week", label: "After 7 Days", days: 7 },
];

export default function DateTimePicker({ value, onChange, testid, placeholder = "Pick date & time" }) {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(value) : null;
  const valid = date && !isNaN(date.getTime());
  const time = valid ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : "11:00";
  const isPast = valid && date.getTime() < Date.now();

  const setDay = (d) => {
    if (!d) return;
    const h = valid ? date.getHours() : 11;
    const m = valid ? date.getMinutes() : 0;
    d.setHours(h, m, 0, 0);
    onChange(toLocalInput(d));
  };

  const setTime = (t) => {
    const base = valid ? new Date(date) : new Date();
    const [h, m] = t.split(":").map(Number);
    base.setHours(h || 0, m || 0, 0, 0);
    onChange(toLocalInput(base));
  };

  // Quick chip: Today -> now + 30min (rounded to clean 5min). Others -> keep current
  // clock time (or the existing/last-picked time) on that future day.
  const applyQuick = (days) => {
    const now = new Date();
    let target;
    if (days === 0) {
      target = roundUp5(new Date(now.getTime() + 30 * 60 * 1000));
    } else {
      target = new Date(now);
      target.setDate(target.getDate() + days);
      if (valid) target.setHours(date.getHours(), date.getMinutes(), 0, 0);
      else target.setHours(now.getHours(), now.getMinutes(), 0, 0);
    }
    onChange(toLocalInput(target));
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {QUICK.map((q) => (
          <button key={q.key} type="button" data-testid={testid ? `${testid}-quick-${q.key}` : undefined}
            onClick={() => applyQuick(q.days)}
            className="rounded-full border border-sky bg-white px-2.5 py-1 text-xs font-medium text-ink/70 transition-colors hover:border-loop hover:text-loop">
            {q.label}
          </button>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className={`flex h-10 w-full items-center justify-between rounded-lg border bg-white px-2.5 text-sm outline-none transition-colors hover:border-loop focus:border-loop focus:ring-2 focus:ring-loop/20 ${isPast ? "border-red-300" : "border-sky"}`}
          >
            <span className={valid ? "text-ink" : "text-ink/40"}>{valid ? fmtDateTime(value) : placeholder}</span>
            <CalendarClock className="h-4 w-4 text-ink/40" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto border-sky p-0" align="start">
          <Calendar mode="single" selected={valid ? date : undefined} onSelect={setDay} initialFocus />
          <div className="flex items-center gap-2 border-t border-sky/60 p-3">
            <span className="text-xs font-medium text-ink/60">Time</span>
            <input
              type="time"
              data-testid={testid ? `${testid}-time` : undefined}
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="h-9 flex-1 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop"
            />
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover">Done</button>
          </div>
        </PopoverContent>
      </Popover>
      {valid && !isPast && (
        <p className="mt-1 text-[11px] text-ink/50" data-testid={testid ? `${testid}-summary` : undefined}>Scheduled for: {fmtDateTime(value)}</p>
      )}
      {isPast && (
        <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-red-500" data-testid={testid ? `${testid}-past-error` : undefined}>
          <AlertCircle className="h-3 w-3" />That time is in the past — please pick a future date & time.
        </p>
      )}
    </div>
  );
}
